import classNames from 'classnames'
import { RefObject, useEffect, useMemo } from 'react'

export const useBulletinBody = (
  body: string,
  ref: RefObject<HTMLDivElement | null>
) => {
  const resolved = useMemo(() => {
    const doc = new DOMParser().parseFromString(body, 'text/html')

    doc.querySelectorAll('img').forEach((img) => {
      // Add image styles
      img.setAttribute(
        'class',
        classNames(
          'rounded-2xl w-full h-auto bg-common-gray-3 min-h-96 transition-transform',
          img.getAttribute('class')
        )
      )

      // Add image caption
      const caption = img.getAttribute('data-caption')

      if (caption) {
        img.removeAttribute('data-caption')

        const imgClone = img.cloneNode() as HTMLImageElement
        const figure = doc.createElement('figure')
        const figcaption = doc.createElement('figcaption')

        figcaption.textContent = caption
        figcaption.setAttribute('class', 'text-sm text-common-gray-7 mt-2')

        figure.appendChild(imgClone)
        figure.appendChild(figcaption)

        img.replaceWith(figure)
      }
    })

    return doc
  }, [body])

  useEffect(() => {
    const refCurrent = ref.current

    // Remove min height when image is loaded
    const handleImageLoad = (img: HTMLImageElement) => {
      const imgClassnames = img.getAttribute('class')

      if (imgClassnames) {
        img.setAttribute('class', imgClassnames.replace('min-h-96 ', ''))
      }
    }

    const listeners: [HTMLImageElement, () => void][] = []

    // Add image load event listeners
    refCurrent?.querySelectorAll('img').forEach((img) => {
      const listener = () => handleImageLoad(img)
      listeners.push([img, listener])
      img.addEventListener('load', listener)
    })

    // Remove image load event listeners
    return () => {
      listeners.forEach(([img, listener]) => {
        img.removeEventListener('load', listener)
      })
    }
  }, [resolved, ref])

  return resolved.body.innerHTML
}
